import { faXmark, faSun, faMoon } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { appContext } from "../../App";

interface NavbarProps {
    navBgColor: string;
    navIconSize: string;
    joinSegmentRef: React.RefObject<HTMLDivElement>;
    priceEstimationRef: React.RefObject<HTMLDivElement>;
    aboutRef: React.RefObject<HTMLDivElement>;
    whyChooseUsRef: React.RefObject<HTMLDivElement>;
    contactRef: React.RefObject<HTMLDivElement>; // Added contactRef
}

const Navbar: React.FC<NavbarProps> = ({
    navBgColor,
    navIconSize,
    joinSegmentRef,
    priceEstimationRef,
    aboutRef,
    whyChooseUsRef,
    contactRef,
}) => {
    const [isNavOpen, setIsNavOpen] = useState(false);
    const navigate = useNavigate();
    const { darkMode, setDarkMode } = useContext(appContext);

    const toggleNav = () => setIsNavOpen(!isNavOpen);

    const navLinks = [
        { label: "Revenue Calculator", ref: priceEstimationRef },
        { label: "Why Choose Us", ref: whyChooseUsRef },
        { label: "Contact Us", ref: contactRef }
    ];

    const isScrolled = navBgColor !== "bg-transparent";

    // Function to scroll to top
    const scrollToTop = () => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
        navigate('/'); // Navigate to home route
    };

    // Function to scroll to a ref
    const scrollToRef = (ref: React.RefObject<HTMLDivElement>) => {
        if (ref.current) {
            const navHeight = 80;
            window.scrollTo({ 
                top: ref.current.offsetTop - navHeight, 
                behavior: 'smooth' 
            });
        }
    };

    return (
        <nav className={`fixed top-0 w-full z-50 transition-all duration-300 ${isScrolled ? 'py-2 md:py-3 border-b border-gray-200/10' : 'py-4 md:py-6'}`}>
            {/* Background Layer with Blur */}
            <div className={`absolute inset-0 transition-all duration-300 ${isScrolled ? 'bg-white/70 dark:bg-slate-950/70 backdrop-blur-xl' : 'bg-transparent'}`}></div>

            <div className="container mx-auto px-4 md:px-6 relative z-10">
                <div className="flex items-center justify-between">
                    {/* Logo - Now scrolls to top */}
                    <div
                        onClick={scrollToTop} // Changed to scrollToTop function
                        className="cursor-pointer group flex items-center"
                    >
                        <img
                            src={darkMode ? "/assets/segpool.png" : "/assets/segpool_blue.png"}
                            className={`transition-all duration-300 ${isScrolled ? 'h-8 md:h-9' : 'h-10 md:h-12'}`}
                            alt="SegPool Logo"
                        />
                    </div>

                    {/* Desktop Navigation */}
                    <div className="hidden lg:flex items-center space-x-1">
                        {navLinks.map((item, index) => (
                            <button
                                key={index}
                                onClick={() => scrollToRef(item.ref)}
                                className={`px-4 py-2 rounded-full text-sm font-bold transition-all duration-200 ${
                                    isScrolled 
                                    ? 'text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800' 
                                    : 'text-slate-700 dark:text-slate-300 hover:bg-slate-100/50 dark:hover:bg-slate-800/50'
                                }`}
                            >
                                {item.label}
                            </button>
                        ))}
                    </div>

                    {/* Actions */}
                    <div className="flex items-center space-x-2 md:space-x-4">
                        {/* Theme Toggle */}
                        <button
                            onClick={() => setDarkMode(!darkMode)}
                            className={`p-2 rounded-xl transition-all duration-300 ${
                                isScrolled 
                                ? 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400' 
                                : 'bg-slate-100/50 dark:bg-slate-800/50 text-slate-600 dark:text-slate-400'
                            }`}
                        >
                            <FontAwesomeIcon icon={darkMode ? faSun : faMoon} />
                        </button>

                        <div className="hidden md:flex items-center space-x-2">
                            <button
                                onClick={() => navigate('/login')}
                                className={`px-5 py-2.5 rounded-2xl text-sm font-black transition-all duration-300 ${
                                    isScrolled 
                                    ? 'text-[#0c2a4a] dark:text-white border border-[#0c2a4a]/10 dark:border-white/10 hover:bg-slate-50 dark:hover:bg-slate-900' 
                                    : 'text-[#0c2a4a] dark:text-white border border-[#0c2a4a]/20 dark:border-white/20 hover:bg-slate-100/50 dark:hover:bg-slate-900/50'
                                }`}
                            >
                                SIGN IN
                            </button>
                            <button
                                onClick={() => navigate('/signup')}
                                className="px-5 py-2.5 bg-yellow-500 hover:bg-yellow-600 active:scale-[0.98] text-white rounded-2xl text-sm font-black shadow-lg shadow-yellow-500/20 transition-all"
                            >
                                START MINING
                            </button>
                        </div>

                        {/* Mobile Menu Button */}
                        <button
                            className="lg:hidden p-2 text-slate-600 dark:text-slate-300"
                            onClick={toggleNav}
                        >
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M4 6h16M4 12h16m-7 6h7" />
                            </svg>
                        </button>
                    </div>
                </div>
            </div>

            {/* Mobile Sidebar */}
            <div className={`fixed inset-0 z-[100] lg:hidden transition-all duration-500 ${isNavOpen ? 'translate-x-0' : 'translate-x-full'}`}>
                {/* Backdrop */}
                <div
                    className={`absolute inset-0 bg-slate-950/60 backdrop-blur-sm transition-opacity duration-500 ${isNavOpen ? 'opacity-100' : 'opacity-0'}`}
                    onClick={toggleNav}
                ></div>

                {/* Side Drawer */}
                <div className="absolute right-0 top-0 bottom-0 w-[280px] bg-white dark:bg-slate-950 shadow-2xl flex flex-col p-6">
                    <div className="flex items-center justify-between mb-8">
                        <img
                            src={!darkMode ? "/assets/segpool_blue.png" : "/assets/segpool.png"}
                            className="h-8"
                            alt="Logo"
                        />
                        <button onClick={toggleNav} className="p-2 text-slate-400 hover:text-slate-600 dark:hover:text-white">
                            <FontAwesomeIcon icon={faXmark} className="text-xl" />
                        </button>
                    </div>

                    <div className="flex flex-col space-y-2 mb-8">
                        {navLinks.map((item, index) => (
                            <button
                                key={index}
                                onClick={() => {
                                    scrollToRef(item.ref);
                                    toggleNav();
                                }}
                                className="flex items-center px-4 py-3 rounded-2xl text-left font-bold text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-900 transition-all"
                            >
                                {item.label}
                            </button>
                        ))}
                    </div>

                    <div className="mt-auto space-y-3">
                        <button
                            onClick={() => { navigate('/login'); toggleNav(); }}
                            className="w-full py-4 rounded-2xl font-black text-slate-700 dark:text-white border border-slate-200 dark:border-slate-800"
                        >
                            SIGN IN
                        </button>
                        <button
                            onClick={() => { navigate('/signup'); toggleNav(); }}
                            className="w-full py-4 bg-yellow-500 text-white rounded-2xl font-black shadow-xl shadow-yellow-500/20"
                        >
                            GET STARTED
                        </button>
                    </div>
                </div>
            </div>
        </nav>
    );
};

export default Navbar;